import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { subscriptionService, getActiveSubscription, createCheckoutSession } from './service';

// Query keys for subscription data
export const subscriptionKeys = {
  all: ['subscriptions'],
  plans: () => [...subscriptionKeys.all, 'plans'],
  activeSubscription: () => [...subscriptionKeys.all, 'active'],
  payments: () => [...subscriptionKeys.all, 'payments'],
  plan: (id) => [...subscriptionKeys.plans(), id],
};

// Hook to fetch subscription plans
export const useSubscriptionPlans = (token, options = {}) => {
  return useQuery(
    subscriptionKeys.plans(),
    async () => {
      if (!token) throw new Error('Token is required');
      return await subscriptionService.getBillingPlans(token);
    },
    {
      enabled: !!token,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: 2,
      ...options,
    }
  );
};

// Hook to fetch active subscription
export const useActiveSubscription = (token, options = {}) => {
  return useQuery(
    subscriptionKeys.activeSubscription(),
    async () => {
      if (!token) return null;
      try {
        const response = await getActiveSubscription(token);
        return response.status === 'success' ? response.data : null;
      } catch (error) {
        // If 404 or no subscription, return null (not an error)
        if (error?.response?.status === 404 || error?.response?.data?.status === 'error') {
          return null;
        }
        throw error;
      }
    },
    {
      enabled: !!token,
      staleTime: 1000 * 60 * 2, // 2 minutes - more frequent updates for active subscription
      cacheTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchInterval: 1000 * 60 * 3, // Refetch every 3 minutes
      retry: (failureCount, error) => {
        // Don't retry on 404 (no subscription found)
        if (error?.response?.status === 404) return false;
        return failureCount < 2;
      },
      ...options,
    }
  );
};

// Hook to fetch subscription payments
export const useSubscriptionPayments = (token, options = {}) => {
  return useQuery(
    subscriptionKeys.payments(),
    async () => {
      if (!token) throw new Error('Token is required');
      const response = await axios.get('/api/subscription/payments', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.data || [];
    },
    {
      enabled: !!token,
      staleTime: 1000 * 60 * 3, // 3 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: 2,
      ...options,
    }
  );
};

// Hook to create checkout session (mutation)
export const useCreateCheckoutSession = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ token, productId, billingCycle }) => {
      return await createCheckoutSession(token, productId, billingCycle);
    },
    {
      onSuccess: () => {
        // Invalidate active subscription query after successful checkout
        queryClient.invalidateQueries(subscriptionKeys.activeSubscription());
        queryClient.invalidateQueries(subscriptionKeys.payments());
      },
    }
  );
};

// Hook to download invoice (mutation)
export const useDownloadInvoice = () => {
  return useMutation(
    async ({ token, paymentId }) => {
      const response = await axios.get(`/api/subscription/invoice/${paymentId}/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });
      return response;
    }
  );
};

